/**
 * Groups hub bulk revoke — batch POST with sequential PUT fallback (GS-BULK-API-1).
 */

import {
  formatGroupShareApplyError,
  isGroupShareDbBusyError,
} from "./group-share-busy-ux.js";

/**
 * @param {string} msg
 */
function _isBulkEndpointMissing(msg) {
  const m = String(msg || "");
  return m.startsWith("404 ") || m.startsWith("405 ") || m.startsWith("501 ");
}

/**
 * @param {Array<{ owner_admin?: string, group_display: string }>} rows
 */
function _normalizeBulkRows(rows) {
  const out = [];
  for (const row of rows) {
    const gdisp = String(row.group_display || "").trim();
    if (!gdisp) continue;
    const ownerA = String(row.owner_admin || "").trim();
    out.push({
      group_display: gdisp,
      ...(ownerA ? { owner_admin: ownerA } : {}),
    });
  }
  return out;
}

/**
 * @param {object} ctx
 * @param {Array<{ owner_admin?: string, group_display: string }>} ctx.rows
 * @param {typeof import("./group-shares-v1.js").syncGroupShareGrantees} ctx.syncGroupShareGrantees
 * @param {typeof import("./group-shares-v1.js").bulkRevokeGroupShareHub} [ctx.bulkRevokeGroupShareHub]
 * @param {(msg: string, kind?: string) => void} [ctx.toast]
 * @returns {Promise<{ ok: number, fail: number, stoppedBusy: boolean, usedBatch?: boolean }>}
 */
export async function revokeGroupsHubBulkRows(ctx) {
  const { rows, syncGroupShareGrantees, bulkRevokeGroupShareHub, toast } = ctx;
  const normalized = _normalizeBulkRows(rows);
  if (!normalized.length) {
    return { ok: 0, fail: rows.length, stoppedBusy: false };
  }

  if (typeof bulkRevokeGroupShareHub === "function") {
    try {
      const res = await bulkRevokeGroupShareHub({
        rows: normalized,
        timeoutMs: 90000,
      });
      const processed = Number(res && res.groups_processed);
      const ok = Number.isFinite(processed) && processed >= 0 ? processed : normalized.length;
      return { ok, fail: Math.max(0, normalized.length - ok), stoppedBusy: false, usedBatch: true };
    } catch (e) {
      const msg = String((e && e.message) || e || "");
      if (_isBulkEndpointMissing(msg)) {
        // Pre-GS-BULK-API-1 server — fall through to sequential PUT.
      } else if (isGroupShareDbBusyError(e)) {
        if (toast) toast(formatGroupShareApplyError(e), "warn");
        return { ok: 0, fail: normalized.length, stoppedBusy: true };
      } else {
        throw e;
      }
    }
  }

  let ok = 0;
  let fail = 0;
  let stoppedBusy = false;
  for (const row of normalized) {
    if (stoppedBusy) {
      fail += 1;
      continue;
    }
    try {
      await syncGroupShareGrantees({
        group_display: row.group_display,
        owner_admin: row.owner_admin,
        grantees: [],
        timeoutMs: 45000,
      });
      ok += 1;
    } catch (e) {
      fail += 1;
      if (isGroupShareDbBusyError(e)) {
        stoppedBusy = true;
        if (toast) toast(formatGroupShareApplyError(e), "warn");
      }
    }
  }
  return { ok, fail, stoppedBusy, usedBatch: false };
}
